import useSWR from 'swr';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Grid, Stack, Typography } from '@mui/material';
import { SaveButton } from '@shared/inputs/Buttons';
import { useUsers } from '@shared/hooks/useUsers';
import { IPeriod } from '@shared/interfaces';
import { SelectAutocomplete } from '@shared/inputs/SelectAutocomplete';
import { SERVICES } from '@shared/constants/apiConfig';
import { formatDate, getOptions, getPeriodOptions } from '@shared/commonFunctions';
import { TextInput } from '@shared/inputs/TextInput';
import { Roles } from '@shared/constants/constants';
import { useUserAccount } from '@shared/hooks/useUserAccount';
import { Wrapper } from '@styles/CommonStyles';
import { SaveButtonWrapper } from '@styles/Admin-tools/Tracker';
import { PhoenixGridHeader } from '../Tools/LenderUtility/shared/PhoenixGridHeader';
import { EGoalType, IGoalLockDate, IUserGoals } from '../Tracker/interfaces';
import { requestGetUserGoals, requestSaveUserGoals, requestUpdateUserGoals } from './requests';

export const Goals = () => {
  const { users } = useUsers();
  const { isAdmin, userId, userHasRoles } = useUserAccount();

  const hasGoalEntryOverrideRole = isAdmin || userHasRoles([Roles.GOAL_ENTRY_OVERRIDE]);

  const [goalsUserId, setGoalsUserId] = useState('');
  const [periodId, setPeriodId] = useState<string | null>(null);
  const [grossGoal, setGrossGoal] = useState(0);
  const [dealsGoal, setDealsGoal] = useState(0);
  const [userGoals, setUserGoals] = useState<IUserGoals[]>([]);
  const { data: periods = [] } = useSWR<IPeriod[]>(`${SERVICES.USER}/period`);
  const { data: goalLockDate = {} as IGoalLockDate} = useSWR<IGoalLockDate>(
    periodId && `${SERVICES.USER}/user-goals/byperiod/${periodId}`
  );
  
  useEffect(() => {
    if (userId) {
      setGoalsUserId(userId);
    }
  }, [userId]);

  useEffect(() => {
    if (goalsUserId && periodId) {
      const fetchUserGoals = async () => {
        const goals = await requestGetUserGoals(goalsUserId, periodId);
        setUserGoals(goals || []);
      };
      fetchUserGoals().catch(console.error);
    } else {
      setGrossGoal(0);
      setDealsGoal(0);
    }
  }, [goalsUserId, periodId]);

  useEffect(() => {
    if (periods.length) {
      const currentDate = new Date();
      const currentMonth = currentDate.getMonth();
      const currentYear = currentDate.getFullYear();

      const currentPeriod = periods.find(
        (period) => period.year === currentYear && moment(period.fromDate).month() === currentMonth
      );

      if (currentPeriod) {
        setPeriodId(currentPeriod.id);
      }
    }
  }, [periods]);

  useEffect(() => {
    if (userGoals.length) {
      const userGoal = userGoals[0];
      setGoalsUserId(userGoal.userId);
      setPeriodId(userGoal.periodId);
      userGoal.goals.forEach((goal) => {
        if (goal.goalType === EGoalType.GrossGoal) {
          setGrossGoal(goal.amount);
        } else {
          setDealsGoal(goal.amount);
        }
      });
    } else {
      setGrossGoal(0);
      setDealsGoal(0);
    }
  }, [userGoals]);

  const userSelectOptions = useMemo(
    () => getOptions(users?.groupUsers ?? [], 'userId', 'userName'),
    [users?.groupUsers]
  );

  const isDisable = useMemo(() => (
    !hasGoalEntryOverrideRole ? (userHasRoles([Roles.LC_MANAGER, Roles.LC_LEAD]) ? goalLockDate.isGoalLocked : true) : false
  ), [goalLockDate.isGoalLocked]);

  const periodOptions = useMemo(() => getPeriodOptions(periods), [periods]);

  const saveGoals = useCallback(
    async () => {
      const request: IUserGoals = {
        userId: goalsUserId || '',
        periodId: periodId || '',
        goals: [
          {
            goalType: EGoalType.GrossGoal,
            amount: grossGoal,
          },
          {
            goalType: EGoalType.DealsGoal,
            amount: dealsGoal,
          },
        ],
      };

      if (userGoals[0]) {
        await requestUpdateUserGoals(request, userGoals[0].id);
      } else {
        await requestSaveUserGoals(request);
      }
    }, 
    [userId, periodId, grossGoal, dealsGoal, userGoals]
  );

  const getPeriod = useCallback(
    () => periods.find((period) => period.id === userGoals[0]?.periodId)?.name ?? '',
    [periods, userGoals]
  );

  const getUser = useCallback(
    () => users?.groupUsers?.find((user) => user.userId === userGoals[0]?.userId)?.userName ?? '',
    [users, userGoals]
  );

  return (
    <Wrapper>
      <Grid
        container
        spacing={4}
      >
        <Grid
          item
          md={4}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
          >
            <PhoenixGridHeader title="Goals" />
            <SaveButtonWrapper>
              <SaveButton
                width="80px"
                onClick={saveGoals}
              />
            </SaveButtonWrapper>
          </Stack>
          <Typography sx={{ mt: 1 }}>Locks on {formatDate(goalLockDate.goalDateTime)}</Typography>
          {hasGoalEntryOverrideRole ? (
            <Stack
              direction="row"
              gap={4}
            >
              <SelectAutocomplete
                value={goalsUserId}
                placeholder="Select Users"
                options={userSelectOptions}
                onChange={(goalsUserId: string) => setGoalsUserId(goalsUserId)}
              />
              <SelectAutocomplete
                label=""
                sx={{ minWidth: 200 }}
                value={periodId}
                options={periodOptions}
                onChange={(period) => setPeriodId(period as string)}
              />
            </Stack>
          ) : (
            <>
              <Typography sx={{ mt: 1 }}>Period {getPeriod()}</Typography>
              <Typography sx={{ mt: 1 }}>Name {getUser()}</Typography>
            </>
          )}
          <Stack
            direction="row"
            gap={4}
            sx={{ mt: 2 }}
          >
            <Typography>Funded Gross Goal</Typography>
            <TextInput
              type="number"
              label=""
              disabled={isDisable}
              value={grossGoal}
              onChange={({ target: { value } }) => setGrossGoal(parseInt(value))}
            />
          </Stack>

          <Stack
            direction="row"
            gap={4}
            sx={{ mt: 1 }}
          >
            <Typography>Funded Deals Goal</Typography>
            <TextInput
              type="number"
              label=""
              disabled={isDisable}
              value={dealsGoal}
              onChange={({ target: { value } }) => setDealsGoal(parseInt(value))}
            />
          </Stack>
        </Grid>
      </Grid>
    </Wrapper>
  );
};
